import type {
  ExportDressingOptions,
  ExportPageOptions,
  ExportRenderFacts,
  ExportTableOptions,
} from '../schemas/export.js';
import type { ExportModel } from './build-export-model.js';
import { type TilePage, applyColumnOrder, computeTilePages } from './compute-tile-pages.js';
import {
  documentFooterEnabled,
  documentHeaderHeightPx,
  documentHeaderVisibility,
} from './document-header.js';
import { resolveExportRowHeights } from './estimate-row-heights.js';
import {
  FOOTER_RESERVED_PX,
  HEADER_ROW_RESERVED_PX,
  type PageGeometry,
  SUBJECT_DESCRIPTION_RESERVED_PX,
  pageGeometry,
} from './page-geometry.js';
import { type PageLayout, buildPageLayout } from './pdf-page-layout.js';
import {
  buildTransposedSections,
  computeTransposedPages,
  transposedUsableHeightPx,
} from './transposed.js';

/**
 * Everything about a PDF export's geometry, derived once (issue #11): what the
 * backend renders from, and what the browser re-derives — from the displayed
 * document's own request and render facts — to lay its overlays on the pages
 * Chromium actually drew.
 */
export interface PdfDocumentPlan {
  /** The export model with `table.columnOrder` applied — what the renderer draws. */
  model: ExportModel;
  geometry: PageGeometry;
  /** The matrix's screen widths with `table.columnWidths` overriding them. */
  columnWidths: Record<string, number>;
  /** Resolved per `table.rowHeightMode` (`resolveExportRowHeights`). */
  rowHeights: Record<string, number>;
  labelColumnCount: 1 | 2;
  /** The document header's pinned height (0 when it draws nothing). */
  headerPx: number;
  /** `FOOTER_RESERVED_PX` when the document draws a footer, else 0. */
  footerPx: number;
  /** One tile per physical page, row-major — `[]` when transposed. */
  tiles: TilePage[];
  /** `buildPageLayout` of each tile, same order — `[]` when transposed. */
  layouts: PageLayout[];
  /** Every physical page: the grid's, then the notes' (`notesPageCount`). */
  pageCount: number;
  /** Pages the « Notes » section adds after the grid (issue #19) - `facts.notesPageCount`. */
  notesPageCount: number;
  /** How many pages wide the poster grid is — 1 when transposed. */
  columnPageCount: number;
}

export interface PlanPdfDocumentInput {
  /** The export model in its natural column order — the plan applies `table.columnOrder`. */
  model: ExportModel;
  page: ExportPageOptions;
  table: ExportTableOptions;
  dressing: ExportDressingOptions;
  subjectDescriptionIncluded: boolean;
  labelColumnCount: 1 | 2;
  matrixTitle: string;
  viewStateColumnWidths: Record<string, number>;
  viewStateRowHeights: Record<string, number>;
  facts: ExportRenderFacts;
}

/**
 * The single derivation of a PDF export's geometry — column order, merged
 * widths, row heights, header/footer reservations, tiles and page layouts.
 *
 * It replaces three hand-kept copies (the export route, the preview and the
 * export page's sidebar hints) that drifted from one another: the preview
 * ignored `columnOrder` and assumed no logo and no dates, and none of them
 * reserved the footer. Every reservation here is one the renderer pins in
 * its stylesheet, which is what makes the layouts exact.
 */
export function planPdfDocument(input: PlanPdfDocumentInput): PdfDocumentPlan {
  const { page, table, dressing, labelColumnCount, facts } = input;
  const model = applyColumnOrder(input.model, table.columnOrder);
  const geometry = pageGeometry(page);
  const contentWidthPx = geometry.pageWidthPx - 2 * geometry.marginPx;
  const columnWidths = { ...input.viewStateColumnWidths, ...table.columnWidths };
  const rowHeights = resolveExportRowHeights({
    model,
    table,
    fontSizePt: page.fontSizePt,
    columnWidths,
    viewStateRowHeights: input.viewStateRowHeights,
  });

  const visibility = documentHeaderVisibility({
    dressing,
    hasCompanyLogo: facts.hasCompanyLogo,
    exportedAt: facts.exportedAt,
    matrixUpdatedAt: facts.matrixUpdatedAt,
  });
  const headerPx = documentHeaderHeightPx({
    title: input.matrixTitle,
    contentWidthPx,
    ...visibility,
  });
  const footerPx = documentFooterEnabled(dressing) ? FOOTER_RESERVED_PX : 0;

  const shared = {
    model,
    geometry,
    columnWidths,
    rowHeights,
    labelColumnCount,
    headerPx,
    footerPx,
  };

  if (table.structure === 'transpose') {
    // The renderer packs transposed sections itself; the plan runs the very
    // same packing so the page count agrees. No tile geometry exists here.
    const sections = buildTransposedSections({
      model,
      fontSizePt: page.fontSizePt,
      density: table.density,
      contentWidthPx,
    });
    const pages = computeTransposedPages(
      sections,
      transposedUsableHeightPx({
        geometry,
        documentHeaderHeightPx: headerPx,
        subjectDescriptionIncluded: input.subjectDescriptionIncluded,
        reservedFooterHeightPx: footerPx,
      }),
    );
    return {
      ...shared,
      tiles: [],
      layouts: [],
      pageCount: pages.length + facts.notesPageCount,
      notesPageCount: facts.notesPageCount,
      columnPageCount: 1,
    };
  }

  const tiles = computeTilePages({
    model,
    columnWidths,
    rowHeights,
    geometry,
    fit: page.fit,
    labelColumnCount,
    reservedHeaderHeightPx:
      HEADER_ROW_RESERVED_PX +
      headerPx +
      (input.subjectDescriptionIncluded ? SUBJECT_DESCRIPTION_RESERVED_PX : 0),
    reservedFooterHeightPx: footerPx,
    rowKeys: model.rows.map((row) => `${row.kind}:${row.key}`),
    pageBreaksBefore: table.pageBreaksBefore,
  });
  const layouts = tiles.map((tile, index) =>
    buildPageLayout({
      tile,
      model,
      columnWidths,
      rowHeights,
      labelColumnCount,
      marginPx: geometry.marginPx,
      documentHeaderHeightPx: headerPx,
      isFirstPage: index === 0,
      subjectDescriptionIncluded: input.subjectDescriptionIncluded,
    }),
  );
  const columnPageCount = tiles.reduce((max, tile) => Math.max(max, tile.columnPageIndex + 1), 1);

  return {
    ...shared,
    tiles,
    layouts,
    pageCount: tiles.length + facts.notesPageCount,
    notesPageCount: facts.notesPageCount,
    columnPageCount,
  };
}
